<script lang="ts">
  import { onMount } from 'svelte';
  import { replace, pop } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import type { ReferralSettingsDTO } from '../lib/types';
  import type { PlayerSettings } from '../lib/player/settings';
  import PlayerSettingsForm from '../components/player/PlayerSettingsForm.svelte';

  let loading = $state(true);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let savedAt = $state<number | null>(null);

  // Збережений знімок — з ним порівнюємо чернетку, щоб знати, чи є що зберігати,
  // і щоб PATCH ніс лише реально змінені поля.
  let saved = $state<ReferralSettingsDTO | null>(null);
  // Розділ «Плеєр» має власну форму й кнопку збереження — сюди лише стартові значення.
  let playerInitial = $state<PlayerSettings | null>(null);

  // Чернетка числових полів тримається рядками: порожній інпут під час
  // редагування не має ставати 0 і не має ламати збереження.
  let percent = $state('');
  let attributionWindowDays = $state('');
  let purchaseWindowDays = $state('');
  let holdDays = $state('');
  let termsMarkdown = $state('');

  function fillForm(s: ReferralSettingsDTO): void {
    percent = String(s.percent);
    attributionWindowDays = String(s.attributionWindowDays);
    purchaseWindowDays = String(s.purchaseWindowDays);
    holdDays = String(s.holdDays);
    termsMarkdown = s.termsMarkdown;
  }

  /** Ціле число в межах, або null якщо поле порожнє/криве. */
  function intOrNull(v: string, min: number, max: number): number | null {
    const t = v.trim();
    if (!/^\d+$/.test(t)) return null;
    const n = Number(t);
    return n >= min && n <= max ? n : null;
  }

  const draft = $derived({
    percent: intOrNull(percent, 0, 100),
    attributionWindowDays: intOrNull(attributionWindowDays, 1, 365),
    purchaseWindowDays: intOrNull(purchaseWindowDays, 1, 365),
    holdDays: intOrNull(holdDays, 0, 365),
  });

  const invalidFields = $derived(
    Object.entries(draft)
      .filter(([, v]) => v === null)
      .map(([k]) => k),
  );
  const valid = $derived(invalidFields.length === 0);

  const dirty = $derived.by(() => {
    if (!saved || !valid) return false;
    return (
      draft.percent !== saved.percent ||
      draft.attributionWindowDays !== saved.attributionWindowDays ||
      draft.purchaseWindowDays !== saved.purchaseWindowDays ||
      draft.holdDays !== saved.holdDays ||
      termsMarkdown !== saved.termsMarkdown
    );
  });

  // Попередження показуємо лише коли % справді змінили — інакше воно стає шумом,
  // який перестають читати.
  const percentChanged = $derived(!!saved && valid && draft.percent !== saved.percent);

  function goBack(): void {
    if (window.history.length > 1) pop();
    else replace('/admin/dashboard');
  }

  async function save(): Promise<void> {
    if (!saved || !valid || saving) return;
    saving = true;
    error = null;
    try {
      const { referral } = await api.adminUpdateReferralSettings({
        percent: draft.percent!,
        attributionWindowDays: draft.attributionWindowDays!,
        purchaseWindowDays: draft.purchaseWindowDays!,
        holdDays: draft.holdDays!,
        termsMarkdown,
      });
      saved = referral;
      fillForm(referral);
      savedAt = Date.now();
    } catch (err) {
      error =
        err instanceof ApiError && err.status === 400
          ? 'Перевір значення — щось поза допустимими межами'
          : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    try {
      const { referral, player } = await api.adminGetSettings();
      playerInitial = player;
      saved = referral;
      fillForm(referral);
    } catch {
      error = 'Не вдалось завантажити налаштування';
    } finally {
      loading = false;
    }
  });
</script>

<div class="screen">
  <div class="admin-header">
    <button type="button" class="back-link" onclick={goBack}>← Назад</button>
    <div class="header-title">Налаштування</div>
    <a href="#/profile" class="admin-exit">Вийти</a>
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if !saved}
      <div class="placeholder error">{error ?? 'Не вдалось завантажити налаштування'}</div>
    {:else}
      <!-- На проді рефералка прихована (REFERRALS_VISIBLE): лишається тільки плеєр. -->
      {#if $session?.referralsVisible}
        <div class="section-title">Реферальна система</div>

        <a class="link-card" href="#/admin/referrals">
          <span>Зведення по реферерах і нарахуваннях</span>
          <span class="link-chevron" aria-hidden="true">›</span>
        </a>

        <label class="field-label" for="ref-percent">Комісія, %</label>
        <input
          id="ref-percent"
          class="field"
          class:invalid={invalidFields.includes('percent')}
          type="number"
          min="0"
          max="100"
          bind:value={percent}
        />
        <div class="hint">
          Скільки відсотків від оплати отримує той, хто привів. Нараховується один раз —
          з першої покупки продукту, якого запрошений раніше не купував.
        </div>

        {#if percentChanged}
          <div class="warn">
            Новий відсоток застосується <strong>лише до майбутніх нарахувань</strong>.
            Уже нараховане залишиться з тим відсотком, який діяв на момент оплати.
          </div>
        {/if}

        <label class="field-label" for="ref-attr">Вікно атрибуції, днів</label>
        <input
          id="ref-attr"
          class="field"
          class:invalid={invalidFields.includes('attributionWindowDays')}
          type="number"
          min="1"
          max="365"
          bind:value={attributionWindowDays}
        />
        <div class="hint">
          Скільки днів після переходу за реферальним посиланням ще можна закріпити реферера.
          Пізніше людина вважається такою, що прийшла сама.
        </div>

        <label class="field-label" for="ref-purchase">Вікно покупки, днів</label>
        <input
          id="ref-purchase"
          class="field"
          class:invalid={invalidFields.includes('purchaseWindowDays')}
          type="number"
          min="1"
          max="365"
          bind:value={purchaseWindowDays}
        />
        <div class="hint">
          Скільки днів після закріплення реферера є на покупку. Оплата пізніше комісії не дає.
        </div>

        <label class="field-label" for="ref-hold">Період утримання, днів</label>
        <input
          id="ref-hold"
          class="field"
          class:invalid={invalidFields.includes('holdDays')}
          type="number"
          min="0"
          max="365"
          bind:value={holdDays}
        />
        <div class="hint">
          Термін повернення коштів. Доки він триває, бонус висить «в очікуванні» — оплатити
          ним не можна. Якщо гроші повернуть, нарахування просто згорає, не встигнувши стати
          доступним.
        </div>

        <label class="field-label" for="ref-terms">Текст умов (необовʼязково)</label>
        <textarea
          id="ref-terms"
          class="field"
          rows="4"
          placeholder="Залиш порожнім — умови зберуться автоматично з чисел вище"
          bind:value={termsMarkdown}
        ></textarea>
        <div class="hint">Показується користувачам на сторінці «Реферальна система».</div>

        {#if error}
          <div class="err">{error}</div>
        {/if}

        <button type="button" class="submit" disabled={!dirty || saving} onclick={save}>
          {saving ? 'Зберігаємо…' : 'Зберегти'}
        </button>

        {#if savedAt !== null && !dirty}
          {#key savedAt}
            <div class="saved-note">Збережено</div>
          {/key}
        {/if}
      {/if}

      {#if playerInitial}
        <PlayerSettingsForm initial={playerInitial} />
      {/if}
    {/if}
  </div>
</div>

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link {
    font-size: 12px;
    color: rgba(251, 246, 236, 0.9);
    text-decoration: none;
    padding: 4px 0;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
  }
  .header-title {
    flex: 1;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
  }
  .admin-exit {
    font-size: 11px;
    opacity: 0.85;
    background: rgba(251, 246, 236, 0.1);
    padding: 5px 11px;
    border-radius: 7px;
    color: var(--text-light);
    text-decoration: none;
  }

  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 18px 100px;
  }
  .placeholder {
    padding: 16px;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 10px;
  }
  .placeholder.error {
    color: var(--status-inactive);
  }

  .section-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 2px;
  }

  /* Вхід у зведення — тут, а не в шапці адмінки: у шапці вже дві дії, а третя
     зробила б її тіснуватою на телефоні. */
  .link-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
    padding: 12px 14px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 11px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--text);
    text-decoration: none;
  }
  .link-chevron {
    font-size: 18px;
    line-height: 1;
    color: var(--text-muted);
  }

  .field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    margin: 14px 0 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .field {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 11px 13px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    margin-bottom: 4px;
  }
  .field:focus {
    border-color: var(--outline);
  }
  .field.invalid {
    border-color: var(--status-inactive);
  }
  textarea.field {
    resize: vertical;
    min-height: 80px;
  }
  .hint {
    font-size: 11px;
    color: var(--text-muted);
    margin: 4px 2px 0;
    line-height: 1.4;
  }

  /* Попередження про зміну % — жовте, бо це не помилка, а наслідок, який треба
     усвідомити перед збереженням. */
  .warn {
    margin: 10px 0 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--warn-bg);
    border: 1px solid var(--warn-border);
    color: var(--status-expiring);
    font-size: 11.5px;
    line-height: 1.45;
  }
  .err {
    margin: 14px 0 0;
    font-size: 12px;
    color: var(--status-inactive);
  }

  .submit {
    width: 100%;
    margin-top: 18px;
    padding: 14px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: opacity 0.15s;
  }
  .submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .saved-note {
    margin-top: 10px;
    text-align: center;
    font-size: 12px;
    color: var(--status-active);
  }
</style>
